<template>
	<div>
		<div class="main">
			<div v-if="!isLoading">
				<div class="flex">
					<BaseDatepicker
						ref="BaseInput.order.datum"
						:required="true"
						:label="$t('repair_plan_date')"
						placeholder="01.01.2023"
						type="tel"
						error-behavior="blur"
						v-model="datum"
						:popover="{ placement: 'right' }"
						@input="updateDate"
						class="md:mr-4 w-full md:w-1/2"
						:max_date="new Date()"
					/>
					<BaseInput
						ref="BaseInput.order.vorgangsnummer"
						:required="true"
						class="md:mr-4 w-full md:w-1/2"
						:label="$t('process_number_billing')"
						:placeholder="$t('process_number')"
						type="text"
						v-model="order.vorgangsnummer"
						:disabled="vorgangsnummerPrefilled"
					/>
				</div>
				<div class="flex">
					<BaseSelect
						v-if="damageCase.process_type === 'comprehensive'"
						ref="receiverType"
						class="md:w-1/2 pr-4"
						v-model="receiverType"
						:options="receiverOptions"
						:required="true"
						@input="checkInsurance"
						:label="$t('receiver')"
					/>
				</div>
				<div class="flex mb-10">
					<CompanyForm
						class="w-full"
						ref="CompanyForm"
						v-if="enterInsurance"
						v-model="insurance"
						:options="insuranceOptions"
						:custom-label="$t('foreign_insurance').toString()"
						:options-label="$t('insurance').toString()"
						:highlight_suffix="$t('add_new')"
						:show-postal-options="true"
					/>
				</div>
			</div>
			<LoadingSpinner v-else />
		</div>
	</div>
</template>

<script>
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import { DamagecasesApiFactory, InsurancesApiFactory, PdfApiFactory } from '@/api/generated';
import defaultConfiguration from '@/api/defaultConfiguration';
import apiDefaults from '@/api/defaultConfiguration';
import { emptyCatch, getStringFromDate } from '@/plugins/Utils';
import Validate from '@/mixins/Validate';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import BaseSelect from '@/components/forms/inputs/BaseSelect.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import CompanyForm from '@/components/damagecases/forms/CompanyForm.vue';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';

export default {
	name: 'RepairPlanOrderForm',
	mixins: [Validate, AppContainerMixin],
	components: { CompanyForm, LoadingSpinner, BaseSelect, BaseDatepicker, BaseInput },
	data() {
		return {
			datum: null,
			order: {
				datum: null,
				vorgangsnummer: null,
			},
			vorgangsnummerPrefilled: false,
			pdfURL: null,
			damageCase: null,
			receiverType: null,
			receiverOptions: [],
			isLoading: false,
			enterInsurance: false,
			insurance: {
				id: null,
				opposite_insurance_state: 'versicherung_bekannt',
			},
			insuranceOptions: [],
		};
	},
	mounted() {
		this.isLoading = true;
		this.receiverOptions = [
			{ display: this.$t('customer'), value: 'customer' },
			{ display: this.$t('insurance'), value: 'insurance' },
		];

		DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
			.damagecasesIdGet(this.$route.params.processId)
			.then(response => {
				this.damageCase = response.data;
				if (response.data && response.data.repairShopReferenceNumber) {
					this.vorgangsnummerPrefilled = true;
					this.order.vorgangsnummer = response.data.repairShopReferenceNumber;
				}
				if (this.damageCase.oppositeInsurance === undefined) {
					InsurancesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.insurancesGet()
						.then(res => {
							this.insuranceOptions = res.data.filter(x => x.is_system_insurance);
							this.isLoading = false;
						})
						.catch(error => {
							this.$swal(this.$t('general.attention').toString(), this.$t('no_companies_found').toString(), 'warning');
						});
				} else {
					this.isLoading = false;
				}
				this.isLoading = false;
			})
			.catch(emptyCatch);
	},
	methods: {
		checkInsurance() {
			if (this.receiverType.value === 'insurance' && this.damageCase.oppositeInsurance === undefined) {
				this.enterInsurance = true;
			} else if (this.receiverType.value === 'customer') {
				this.enterInsurance = false;
			}
		},
		updateDate(value) {
			this.order.datum = getStringFromDate(value, ({ day, month, year }) => [year, month, day].join('-'));
		},
		updateDamagecase() {
			return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdOppositeInsurancePut(parseInt(this.$route.params.processId || this.$route.params.id), this.insurance)
				.then(response => {})
				.catch(error => {
					this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				});
		},
		save() {
			if (!this.validate()) return;
			if (this.enterInsurance && this.damageCase.process_type === 'comprehensive') {
				const isForeignInsurance = this.insurance.id === null;
				this.insurance.opposite_insurance_state = 'versicherung_bekannt';
				if (isForeignInsurance) {
					if (this.$refs.CompanyForm.validate()) {
						this.updateDamagecase().then(_ => {
							this.saveDocument();
						});
					} else {
						this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
					}
				} else {
					this.updateDamagecase().then(_ => {
						this.saveDocument();
					});
				}
			} else {
				this.saveDocument();
			}
		},
		saveDocument() {
			if (this.damageCase.process_type === 'comprehensive') {
				this.order.receiverType = this.receiverType.value;
			}
			PdfApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
				.pdfDamagecasesTypeSavePost('repairplan_order', { id: this.$route.params.processId, order: this.order })
				.then(response => {
					let reader = null;
					let blobString = null;
					new PDFDamagecasesRepository().getSavedDocument(response.data.uuid).then(result => {
						this.documentName = result.headers['content-disposition'].split('filename=')[1];
						const blob = new Blob([result.data], { type: 'application/pdf' });
						if (this.isAppContainer) {
							reader = new FileReader();
							const name = this.documentName;
							reader.onload = function() {
								const arrayBuffer = this.result;
								// convert the ArrayBuffer to a comma-separated string
								const dataString = new Uint8Array(arrayBuffer).join(',');
								const messageData = {
									fileName: name,
									data: dataString,
								};
								// send the data to Swift
								window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
							};
							blobString = blob;
						} else {
							this.pdfURL = URL.createObjectURL(blob) + '#toolbar=1&navpanes=0&scrollbar=0';
						}
					});
					this.$swal(this.$t('general.success').toString(), this.$t('pdf_with_order_form_generated').toString(), 'success').then(() => {
						this.$store.commit('setConfirmLeaving', false);
						if (!this.isAppContainer) {
							const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(this.pdfURL)}&title=${encodeURIComponent(this.documentName)}`;

							setTimeout(function() {
								window.open(viewerUrl, '_blank');
								window.URL.revokeObjectURL(viewerUrl);
							}, 200);
						} else {
							reader.readAsArrayBuffer(blobString);
						}
						window.URL.revokeObjectURL(this.pdfURL);
						this.$store.dispatch('clearModalHistory');
						this.$store.dispatch('forceUpdateDamageCase');
						this.$router.back();
					});
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('pdf_generation_error').toString(), 'error');
				});
		},
	},
};
</script>

<style scoped></style>
